import React, { memo } from 'react';
import Div from '@/shared/Div';
import Container from '@/shared/Container';
import Heading from '@/shared/Heading';
import Paragraph from '@/shared/Paragraph';
import Button from '@/shared/Button';
import Map from '@/components/Map';
import withContentFlags from '@/hoc/withContentFlags';
import Image from '../../shared/Image';

interface Report {
  icon: string;
  title: string;
  subtitle: string;
  link: string;
}

export interface AnnualReportsProps {
  title: string;
  data: Report[];
  cta: any;
}

const AnnualReports = (props: AnnualReportsProps) => {
  const { title, data = [], cta } = props;

  const renderReport = (report: Report, index: number) => {
    const { icon, title: reportTitle, subtitle, link } = report;

    return (
      <Div
        key={index}
        className="flex flex-col gap-s4 items-start w-full min-w-[calc(100%-3rem)] md:min-w-0"
      >
        {/* Report Image */}
        <Image src={icon} alt={reportTitle} className="w-full h-full object-cover" />

        {/* Report Title, Subtitle and Download Icon */}
        <Div className="flex justify-between items-start self-stretch">
          <Div className="flex flex-col gap-s1">
            <Paragraph className="text-body-lg  text-cm-primary">{reportTitle}</Paragraph>
            <Heading level="3" className="text-h-3 ">
              {subtitle}
            </Heading>
          </Div>

          {/* Download Icon */}
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center cursor-pointer"
          >
            <Image
              src="/images/file_save.svg"
              alt="Download"
              width={50}
              height={50}
              className="w-full h-auto"
            />
          </a>
        </Div>
      </Div>
    );
  };

  return (
    <Div className="bg-cs-secondary py-7 md:py-20">
      <Container className="flex flex-col justify-center items-center gap-s7">
        {/* Header Section with Title and CTA */}
        <Div className="flex justify-between items-center self-stretch">
          <Heading level="1" className="text-hd-1 md:text-desktop-h-1  text-cm-brand">
            {title}
          </Heading>

          {/* CTA Button - Hidden on Mobile, Visible on Desktop */}
          {cta && (
            <Button
              label={cta.label}
              href={cta.href}
              target={cta.target}
              theme={cta.theme}
              size={cta.size}
              dataTestId="annual-reports-cta"
              rounded
              className="hidden md:flex"
            >
              {cta.label}
            </Button>
          )}
        </Div>

        {/* Reports Grid */}
        <Div className="flex overflow-x-auto hide-scrollbar px-s6 -mx-6 md:p-0 md:mx-0 gap-s6 md:grid md:grid-cols-2 lg:grid-cols-4 self-stretch">
          <Map list={data} render={renderReport} />
        </Div>

        {/* CTA Button - Visible on Mobile Only, Below Reports */}
        {cta ? (
          <Button
            label={cta.label}
            href={cta.href}
            target={cta.target}
            theme={cta.theme}
            size={cta.size}
            dataTestId="annual-reports-cta-mobile"
            rounded
            className="md:hidden self-start"
          >
            {cta.label}
          </Button>
        ) : null}
      </Container>
    </Div>
  );
};

export default withContentFlags(memo(AnnualReports));
